import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { FlightBookingApiService } from '../../../core/api-services/flight-booking-api.service';
import { AuthorizationService } from '../../../core/authorization/authorization.service';
import { Permissions } from '../../../core/authorization/permissions';
import {
  FlightConfirmationImportResult,
  FlightDecisionReasonCode,
  FlightRequestDetail,
  FlightRequestStatus,
  FlightRequestSummary,
} from '../../../core/models/flight-request';

/**
 * Reviewer queue for student flight booking requests.
 *
 * Supports the whole reviewer job: filter and triage the queue, approve or
 * reject (a rejection always carries a reason), bulk decide, export the queue
 * for a booking company, and import their confirmations back in.
 */
@Component({
  selector: 'app-admin-flight-requests',
  standalone: true,
  imports: [CommonModule, DatePipe, ReactiveFormsModule],
  templateUrl: './flight-requests.html',
  styleUrl: './flight-requests.scss',
})
export class AdminFlightRequestsComponent implements OnInit {
  private readonly api = inject(FlightBookingApiService);
  private readonly fb = inject(FormBuilder);
  readonly authorization = inject(AuthorizationService);
  readonly permissions = Permissions;

  readonly statuses: FlightRequestStatus[] = ['SUBMITTED', 'APPROVED', 'REJECTED', 'BOOKED', 'CANCELLED'];

  readonly reasonCodes: { value: FlightDecisionReasonCode; label: string }[] = [
    { value: 'CHEAPER_OPTION_AVAILABLE', label: 'A cheaper option is available' },
    { value: 'COST_LIMIT_EXCEEDED', label: 'Cost over the allowed budget' },
    { value: 'FLIGHT_LIMIT_REACHED', label: 'Flight allocation reached' },
    { value: 'PAID_FLIGHT_NOT_ALLOWED', label: 'Paid flights not allowed' },
    { value: 'NOT_ELIGIBLE', label: 'Not eligible' },
    { value: 'INCOMPLETE_DETAILS', label: 'Incomplete details' },
    { value: 'OTHER', label: 'Other' },
  ];

  readonly requests = signal<FlightRequestSummary[]>([]);
  readonly selected = signal<FlightRequestDetail | null>(null);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly statusFilter = signal<FlightRequestStatus | ''>('');
  readonly search = signal('');
  readonly chosen = signal<Set<number>>(new Set<number>());
  readonly importResult = signal<FlightConfirmationImportResult | null>(null);

  readonly filters = this.fb.group({
    status: [''],
    search: [''],
  });

  readonly decisionForm = this.fb.group({
    reasonCode: ['CHEAPER_OPTION_AVAILABLE' as FlightDecisionReasonCode],
    reason: ['', Validators.required],
  });

  readonly bookingForm = this.fb.group({
    bookingReference: ['', Validators.required],
    bookingProvider: [''],
    ticketNumber: [''],
    cabinClass: [''],
    cost: [null as number | null],
    currency: ['ZAR'],
    travelDate: [''],
    notes: [''],
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.api
      .getRequestsForReview(this.statusFilter() || null, this.search() || undefined)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (requests) => {
          this.requests.set(requests ?? []);
          this.chosen.set(new Set<number>());
        },
        error: () => this.error.set('The flight request queue could not be loaded.'),
      });
  }

  applyFilters(): void {
    this.statusFilter.set((this.filters.value.status as FlightRequestStatus) || '');
    this.search.set(this.filters.value.search ?? '');
    this.load();
  }

  open(request: FlightRequestSummary): void {
    this.error.set('');
    this.message.set('');
    this.importResult.set(null);
    this.api.getRequestForReview(request.id).subscribe({
      next: (detail) => {
        this.selected.set(detail);
        this.bookingForm.reset({
          bookingReference: detail.bookingReference ?? '',
          bookingProvider: detail.bookingProvider ?? '',
          ticketNumber: detail.ticketNumber ?? '',
          cabinClass: detail.bookedCabinClass ?? detail.preferredCabinClass ?? '',
          cost: detail.bookedCost ?? null,
          currency: detail.bookedCurrency ?? 'ZAR',
          travelDate: detail.bookedTravelDate ? detail.bookedTravelDate.substring(0, 10) : '',
          notes: detail.bookingNotes ?? '',
        });
      },
      error: () => this.error.set('The request could not be loaded.'),
    });
  }

  close(): void {
    this.selected.set(null);
  }

  toggleChosen(id: number): void {
    const next = new Set(this.chosen());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.chosen.set(next);
  }

  isChosen(id: number): boolean {
    return this.chosen().has(id);
  }

  selectableIds(): number[] {
    return this.requests().filter((request) => request.status === 'SUBMITTED').map((request) => request.id);
  }

  toggleAll(): void {
    const selectable = this.selectableIds();
    this.chosen.set(this.chosen().size === selectable.length ? new Set<number>() : new Set(selectable));
  }

  approve(request: FlightRequestSummary): void {
    this.runAction(request.id, { decision: 'Approve' }, 'Request approved.');
  }

  reject(request: FlightRequestSummary): void {
    if (this.decisionForm.invalid) {
      this.decisionForm.markAllAsTouched();
      return;
    }
    const { reasonCode, reason } = this.decisionForm.getRawValue();
    this.runAction(request.id, { decision: 'Reject', reasonCode, reason }, 'Request rejected and the student has been told why.');
  }

  bulkApprove(): void {
    this.bulkDecide('Approve');
  }

  bulkReject(): void {
    this.bulkDecide('Reject');
  }

  private bulkDecide(decision: 'Approve' | 'Reject'): void {
    const ids = Array.from(this.chosen());
    if (ids.length === 0) {
      this.error.set('Select at least one submitted request first.');
      return;
    }
    if (decision === 'Reject' && this.decisionForm.invalid) {
      this.decisionForm.markAllAsTouched();
      return;
    }

    const { reasonCode, reason } = this.decisionForm.getRawValue();
    this.busy.set(true);
    this.error.set('');
    this.message.set('');
    this.api
      .bulkDecide(ids, { decision, reasonCode, reason })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (result) => {
          this.message.set(`${decision === 'Approve' ? 'Approved' : 'Rejected'} ${result.succeeded} of ${result.requested} request(s).`);
          if (result.skipped > 0) {
            this.error.set(
              `Skipped ${result.skipped}: ${result.skips.map((skip) => `${skip.referenceNumber ?? skip.id} (${skip.reason})`).join('; ')}`
            );
          }
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The bulk action failed.'),
      });
  }

  private runAction(
    id: number,
    decision: { decision: 'Approve' | 'Reject'; reasonCode?: FlightDecisionReasonCode | null; reason?: string | null },
    successMessage: string
  ): void {
    this.busy.set(true);
    this.error.set('');
    this.message.set('');
    this.api
      .decide(id, decision)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (detail) => {
          this.selected.set(detail);
          this.message.set(successMessage);
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The action could not be completed.'),
      });
  }

  attachBooking(): void {
    const request = this.selected();
    if (!request || this.bookingForm.invalid) {
      this.bookingForm.markAllAsTouched();
      return;
    }

    const value = this.bookingForm.getRawValue();
    this.busy.set(true);
    this.api
      .attachBooking(request.id, {
        bookingReference: value.bookingReference ?? '',
        bookingProvider: value.bookingProvider || undefined,
        ticketNumber: value.ticketNumber || undefined,
        cabinClass: value.cabinClass || undefined,
        cost: value.cost ?? null,
        currency: value.currency || undefined,
        travelDate: value.travelDate || null,
        notes: value.notes || undefined,
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (detail) => {
          this.selected.set(detail);
          this.message.set('Booking details attached and the student has been notified.');
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The booking details could not be saved.'),
      });
  }

  exportCsv(): void {
    this.busy.set(true);
    this.api
      .exportRequests(this.statusFilter() || null)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `flight-requests-${new Date().toISOString().substring(0, 10)}.csv`;
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        },
        error: () => this.error.set('The queue could not be exported.'),
      });
  }

  onImportFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.busy.set(true);
    this.error.set('');
    this.message.set('');
    this.api
      .importConfirmations(file)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (result) => {
          this.importResult.set(result);
          this.message.set(`Imported ${result.booked} of ${result.totalRows} confirmation(s).`);
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The confirmations could not be imported.'),
      });

    input.value = '';
  }

  statusClass(status: FlightRequestStatus): string {
    switch (status) {
      case 'APPROVED':
        return 'badge badge-success';
      case 'REJECTED':
        return 'badge badge-danger';
      case 'BOOKED':
        return 'badge badge-primary';
      case 'CANCELLED':
        return 'badge badge-muted';
      default:
        return 'badge badge-warning';
    }
  }

  canDecide(status: FlightRequestStatus): boolean {
    return status === 'SUBMITTED';
  }

  canBook(detail: FlightRequestDetail): boolean {
    // The booking can only be attached once the request has been approved.
    return detail.status === 'APPROVED';
  }
}
